var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var ApplicationBar_1;
import React, { Component, createRef, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { CustomModalEvent, Slidebar } from '@philips-internal/oswald-ui-components';
import { Placement } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { ApplicationContextListenerScope } from '@philips-internal/oswald-ui-core/lib/component/context/index.js';
import { arrayOf, bool, element, eventListener, expression, oneOf, oneOfType, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { AuthenticationService, EditorService, HistoryService, MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { classNames, enumValues, getSlot, isTouchableDevice } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as MenuIcon } from '../../../resources/assets/DLS_Menu.svg';
import { ReactComponent as NavigationLeftIcon } from '../../../resources/assets/DLS_Navigation.svg';
import { FoundationApplicationContext } from '../../context/foundation-application-context.js';
import { flattenRoutes, getMainRoute, guessModuleBasedOnRoute, mapRoutes, routeArrayToMap } from '../../util/application-routes.js';
import { ModuleSelectorType } from '../module-selector/module-selector-constants.js';
import { ModuleSelector } from '../module-selector/module-selector.js';
import { Spotlight } from '../spotlight/spotlight.js';
import ApplicationBarAPI from './application-bar-api.js';
import { ApplicationBarMenuType } from './application-bar-constants.js';
import { ApplicationBarTitle } from './components/application-bar-title.js';
import { AuxiliaryBar } from './components/auxiliary-bar.js';
import { OpenModules } from './components/open-modules.js';
import { Routes } from './components/routes.js';
import { ThreeDotIcon } from './components/three-dot-icon.js';
import './application-bar.css';
const APPLICATION_BAR_CLASS_MODIFIER = 'c-application-bar--sticky-to-top';
const APPLICATION_BAR_PARENT_CLASS_MODIFIER = 'c-application-bar--sticky-to-top-parent';
const CUSTOM_SCROLL_EVENT_NAME = 'oswald-application-bar-scroll';
const SCROLL_ELEMENT_CLASS = '.c-foundation-application__content';
const IconsContainer = {
    acceptedTypes: [
        ComponentType.DATA_DISPLAY,
        ComponentType.LAYOUT,
        ComponentType.NAVIGATION,
    ],
    name: 'icons',
};
const UserContentContainer = {
    acceptedTypes: [
        ComponentType.DATA_DISPLAY,
        ComponentType.LAYOUT,
        ComponentType.NAVIGATION,
    ],
    name: 'user',
};
let ApplicationBar = ApplicationBar_1 = class ApplicationBar extends Component {
    constructor() {
        super(...arguments);
        this.listeners = [];
        this.mainContainerRef = createRef();
        // Disabling assertion because Foundation should always be present when using the ApplicationBar.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.scrollElement = document.querySelector(SCROLL_ELEMENT_CLASS);
        this.lastScrollPosition = this.getScrollTop();
        this.state = {
            currentRoute: '',
            modules: [],
            openPopover: false,
            openRoutes: false,
            openSpotlight: false,
            routeMap: new Map(),
            showNavigation: true,
            title: '',
        };
        this.handleScroll = () => {
            const { showOnScrollUp } = this.props;
            const scrollTop = this.getScrollTop();
            const element = this.mainContainerRef.current;
            const isApplicationBarVisible = document.elementFromPoint(0, 0)?.className.includes('c-application-bar');
            const height = scrollTop + window.innerHeight;
            const scrollHeight = this.scrollElement.scrollHeight;
            if (!element || !showOnScrollUp || height > scrollHeight) {
                return;
            }
            if (scrollTop > this.lastScrollPosition) {
                this.updateDOMElements(false);
                this.notifyStickyComponents(false);
            }
            if (scrollTop < this.lastScrollPosition && !isApplicationBarVisible) {
                this.updateDOMElements(true);
                this.notifyStickyComponents(true);
            }
            this.lastScrollPosition = Math.max(0, scrollTop);
        };
        this.handleModalMount = (event) => {
            if (!isTouchableDevice()) {
                return;
            }
            if (event.detail?.showNavigation === false) {
                this.setState({ showNavigation: false });
            }
            this.updateDOMElements(true);
        };
        this.handleModalUnmount = () => {
            if (!isTouchableDevice()) {
                return;
            }
            const { showNavigation } = this.state;
            if (!showNavigation) {
                this.setState({ showNavigation: true });
            }
            this.updateDOMElements(false);
        };
        this.onDotClick = () => {
            this.setState({ openRoutes: true });
        };
        this.onMenuClick = () => {
            this.setState({ openSpotlight: true });
        };
        this.onModalClose = () => {
            this.setState({ openSpotlight: false });
        };
        this.onModuleClick = (module) => {
            this.context.openModule(module.id);
        };
        this.onModuleCloseIconClick = (event, module) => {
            this.context.closeModule(module.id);
            event.stopPropagation();
            event.preventDefault();
        };
        this.onBackNavigationClick = () => {
            if (!this.context.hasNavigation()) {
                return;
            }
            this.context.popNavigationItem();
        };
        this.onReturnToTheBeginningClick = () => {
            const { mainRoute } = this.state;
            if (!mainRoute) {
                return;
            }
            this.context.clearNavigationStack();
            this.setState({ currentRoute: mainRoute.id, openRoutes: false }, () => {
                this.redirectToRoute(mainRoute);
            });
        };
        this.onRouteClick = (route, previous) => {
            this.context.pushNavigationItem(() => {
                this.setState({ currentRoute: previous });
                HistoryService.back();
            });
            this.setState({ currentRoute: route.id, openRoutes: false }, () => {
                this.redirectToRoute(route);
            });
        };
        this.onRoutesModalClose = () => {
            this.setState({ openRoutes: false });
        };
        this.onPopoverModuleClick = (module) => {
            this.setState({ openPopover: false });
            this.onModuleClick(module);
        };
    }
    componentDidMount() {
        const modulesListener = this.context.addListener(ApplicationContextListenerScope.MODULES, () => this.updateModules());
        const navigationListener = this.context.addListener(ApplicationContextListenerScope.NAVIGATION, () => this.forceUpdate());
        const optionsListener = this.context.addListener(ApplicationContextListenerScope.OPTIONS, () => this.forceUpdate());
        const routesListener = this.context.addRedirectListener((args) => this.updateCurrentRoute(args.path));
        const titleListener = this.context.addListener(ApplicationContextListenerScope.TITLE, () => this.updateTitle());
        this.listeners.push(modulesListener, navigationListener, optionsListener, routesListener, titleListener);
        this.guessModule();
        this.updateTitle();
        this.scrollElement?.addEventListener('scroll', this.handleScroll, false);
        this.mainContainerRef.current?.addEventListener(CustomModalEvent.MOUNT, this.handleModalMount, false);
        this.mainContainerRef.current?.addEventListener(CustomModalEvent.UNMOUNT, this.handleModalUnmount, false);
    }
    componentWillUnmount() {
        this.listeners.forEach((listener) => listener());
        this.scrollElement?.removeEventListener('scroll', this.handleScroll, false);
        this.mainContainerRef.current?.removeEventListener(CustomModalEvent.MOUNT, this.handleModalMount, false);
        this.mainContainerRef.current?.removeEventListener(CustomModalEvent.UNMOUNT, this.handleModalUnmount, false);
    }
    getScrollTop() {
        return this.scrollElement?.scrollTop ?? 0;
    }
    updateDOMElements(accomodate) {
        const element = this.mainContainerRef.current;
        const method = accomodate ? 'add' : 'remove';
        if (!element) {
            return;
        }
        element.classList[method](APPLICATION_BAR_CLASS_MODIFIER);
        element.parentElement?.classList[method](APPLICATION_BAR_PARENT_CLASS_MODIFIER);
    }
    notifyStickyComponents(accomodate) {
        const informationBar = document.querySelector('.c-information-bar');
        const customEvent = new CustomEvent(CUSTOM_SCROLL_EVENT_NAME, {
            detail: {
                accomodateApplicationBar: accomodate,
            },
        });
        informationBar?.dispatchEvent(customEvent);
    }
    getSiblingRoutes() {
        const { currentRoute, routeMap, module } = this.state;
        const route = routeMap.get(currentRoute);
        if (!route || !module) {
            return [];
        }
        const hiddenOptions = this.context.getModuleHiddenOptions(module.id);
        const routes = [];
        route.siblings?.forEach((sibling) => {
            const route = routeMap.get(sibling);
            if (route && !hiddenOptions?.has(route.name)) {
                routes.push(route);
            }
        });
        return routes;
    }
    guessModule() {
        if (EditorService.isEditor()) {
            return;
        }
        const module = guessModuleBasedOnRoute();
        if (module) {
            this.context.openModule(module.id);
        }
        else {
            AuthenticationService.redirectToHome();
        }
    }
    redirectToRoute(routeObject) {
        const { completeRoute, route } = routeObject;
        if (completeRoute) {
            return HistoryService.push(completeRoute);
        }
        HistoryService.push(route);
    }
    updateCurrentRoute(path) {
        const { routeMap } = this.state;
        const routes = Array.from(routeMap.values());
        const route = routes.find(route => route.completeRoute === path);
        if (!route) {
            return;
        }
        this.setState({ currentRoute: route.id });
    }
    updateModules() {
        const module = this.context.getCurrentModule();
        const openModules = this.context.getOpenModules();
        let currentRoute = '';
        let mainRoute;
        let routeMap = new Map();
        if (module) {
            const mappedRoutes = mapRoutes(module.id, module.route, module.routes);
            mainRoute = getMainRoute(mappedRoutes);
            currentRoute = mainRoute?.id ?? module.id;
            routeMap = routeArrayToMap(flattenRoutes(mappedRoutes));
            const root = {
                ...module,
                children: mappedRoutes,
                completeRoute: module.route,
            };
            routeMap.set(module.id, root);
        }
        this.setState({
            currentRoute,
            mainRoute,
            module,
            modules: openModules,
            openRoutes: false,
            openSpotlight: false,
            routeMap,
        });
    }
    updateTitle() {
        this.setState({ title: this.context.getTitle() });
    }
    renderAuxiliaryBar() {
        const { currentRoute, routeMap } = this.state;
        const routes = this.getSiblingRoutes();
        const route = routeMap.get(currentRoute);
        if (routes.length <= 0 || !route) {
            return null;
        }
        return (React.createElement(AuxiliaryBar, { className: "c-application-bar__auxiliary-bar", onClick: this.onRouteClick, onReturnClick: this.onReturnToTheBeginningClick, route: route, routes: routes }));
    }
    renderSiblingRoutesModal() {
        const { currentRoute, openRoutes } = this.state;
        const routes = this.getSiblingRoutes();
        if (!openRoutes || !routes.length) {
            return null;
        }
        return (React.createElement(Routes, { onClick: this.onRouteClick, onClose: this.onRoutesModalClose, onReturnClick: this.onReturnToTheBeginningClick, route: currentRoute, routes: routes }));
    }
    renderDots() {
        const { openRoutes } = this.state;
        const routes = this.getSiblingRoutes();
        if (openRoutes || !routes.length) {
            return null;
        }
        return (React.createElement(ThreeDotIcon, { "aria-label": MessageService.translate('tws.application-bar.three-dot-icon-aria-label'), className: "c-application-bar__icon c-application-bar__dot", onClick: this.onDotClick }));
    }
    renderNavigation() {
        const { showMenu } = this.props;
        const { showNavigation } = this.state;
        if (!showNavigation) {
            return null;
        }
        if (this.context.hasNavigation()) {
            return (React.createElement(NavigationLeftIcon, { "aria-label": MessageService.translate('tws.application-bar.navigate-left-icon-aria-label'), className: "c-application-bar__icon c-application-bar__nav", onClick: this.onBackNavigationClick }));
        }
        if (showMenu) {
            return (React.createElement(MenuIcon, { "aria-label": MessageService.translate('tws.application-bar.menu-icon-aria-label'), className: "c-application-bar__icon c-application-bar__menu", onClick: this.onMenuClick }));
        }
        return null;
    }
    renderOpenModules() {
        const { moreDropdownExpression = ApplicationBar_1.defaultProps.moreDropdownExpression } = this.props;
        const modules = this.context.getOpenModules();
        const currentModule = this.context.getCurrentModule();
        return (React.createElement(OpenModules, { currentModule: currentModule, modules: modules, moreDropdownExpression: moreDropdownExpression, onModuleClick: this.onModuleClick, onModuleCloseIconClick: this.onModuleCloseIconClick }));
    }
    renderLinearModules() {
        const element = (React.createElement(ModuleSelector, { moduleType: ModuleSelectorType.LINEAR, onClose: this.onModalClose, title: this.context.getTitle() }));
        return createPortal(element, document.body);
    }
    renderSpotlightModal() {
        const { menuType, onClick } = this.props;
        const { openSpotlight } = this.state;
        if (!openSpotlight) {
            return null;
        }
        if (menuType === ApplicationBarMenuType.LINEAR) {
            return this.renderLinearModules();
        }
        return (React.createElement(Slidebar, { className: "c-application-bar__spotlight-slidebar", onClose: this.onModalClose, open: openSpotlight, placement: Placement.LEFT, showCloseButton: true, title: this.context.getTitle(), uuid: "application-bar-slider-uuid" },
            React.createElement(Spotlight, { applicationTitle: this.context.getTitle(), hideTitle: true, onClick: onClick, onClose: this.onModalClose, subMenuSlideHandler: true })));
    }
    /**
     * The title being rendered differs based on the environemnt that the application is being rendered on.
     *
     * When rendering on a desktop environment, the title should come from the context, which is normally the application name.
     * When rendering on a mobile environment, the title should come from the route that the application bar knows at the moment.
     */
    renderTitle() {
        const { module } = this.state;
        const title = this.context.getTitle();
        return (React.createElement(ApplicationBarTitle, { applicationTitle: title, module: module }));
    }
    renderIconsSlot() {
        const { children, uuid } = this.props;
        const icons = getSlot(children, 'icons');
        const className = classNames({
            'c-application-bar__container': true,
            'c-application-bar__container--icons-hidden': this.context.hasNavigation(),
        });
        return (React.createElement(Container, { acceptedTypes: IconsContainer.acceptedTypes, className: className, slot: IconsContainer.name, uuid: uuid }, icons));
    }
    renderIconsSlotContent() {
        const routes = this.getSiblingRoutes();
        if (isTouchableDevice()) {
            return routes.length
                ? null
                : this.renderIconsSlot();
        }
        return this.renderIconsSlot();
    }
    renderContent() {
        const { children, uuid } = this.props;
        const user = getSlot(children, 'user');
        return (React.createElement(Fragment, null,
            this.renderIconsSlotContent(),
            React.createElement(Container, { acceptedTypes: UserContentContainer.acceptedTypes, className: "c-application-bar__container", slot: UserContentContainer.name, uuid: uuid }, user)));
    }
    render() {
        const className = classNames({
            'c-application-bar': true,
            'c-application-bar--is-touchable-device': isTouchableDevice(),
        });
        return (React.createElement(Fragment, null,
            React.createElement("div", { className: className, ref: this.mainContainerRef },
                this.renderNavigation(),
                this.renderTitle(),
                this.renderOpenModules(),
                this.renderContent(),
                this.renderDots(),
                this.renderSpotlightModal(),
                this.renderSiblingRoutesModal()),
            this.renderAuxiliaryBar()));
    }
};
ApplicationBar.contextType = FoundationApplicationContext;
ApplicationBar.propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    menuType: oneOf(enumValues(ApplicationBarMenuType)),
    moreDropdownExpression: expression,
    onClick: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    showMenu: bool,
    showOnScrollUp: bool,
    uuid: string,
};
ApplicationBar.defaultProps = {
    menuType: ApplicationBarMenuType.SPOTLIGHT,
    moreDropdownExpression: 'tws.application-bar.more-dropdown',
    showMenu: true,
    showOnScrollUp: false,
};
ApplicationBar = ApplicationBar_1 = __decorate([
    AwesomeComponent({ api: ApplicationBarAPI, containers: [IconsContainer, UserContentContainer], type: ComponentType.NAVIGATION })
], ApplicationBar);
export default ApplicationBar;
//# sourceMappingURL=application-bar.js.map